import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { FileText } from "lucide-react";
import {
  OutcomeItem,
  IncomeItem,
  BankStatementMovement,
  BankMovementFinanceiroLine,
  outcomeParcelValorExibido,
  fetchBillsNotes,
} from "@/services/financeiro";
import { noteTextForItem, seedNotesMapFromItems } from "../noteHelpers";
import { formatCurrency, formatDate } from "@/utils/format";
import { cn } from "@/utils/cn";

/* ─── ModalEntityTitle ────────────────────────────────────────────────────── */
interface ModalEntityTitleProps {
  icon:      React.ReactNode;
  label:     string;
  iconBg?:   string;
  iconColor?: string;
}

export function ModalEntityTitle({ icon, label, iconBg = "bg-brand-50", iconColor = "text-brand-600" }: ModalEntityTitleProps) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", iconBg, iconColor)}>
        {icon}
      </div>
      <span className="text-sm font-semibold text-slate-800 leading-tight">{label}</span>
    </div>
  );
}

/* ─── Tabela de outcome (contas a pagar) ─────────────────────────────────── */

const OUTCOME_PER_PAGE = 20;

function OutcomeNotesIcon({ tooltipText }: { tooltipText: string }) {
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);
  const hovering = useRef(false);
  return (
    <>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-600 transition-colors"
        aria-label={tooltipText.startsWith("Carregando") ? "Carregando observações" : "Notas e observações"}
        onMouseEnter={(e) => {
          hovering.current = true;
          setTipPos({ x: e.clientX, y: e.clientY });
        }}
        onMouseMove={(e) => {
          if (hovering.current) setTipPos({ x: e.clientX, y: e.clientY });
        }}
        onMouseLeave={() => {
          hovering.current = false;
          setTipPos(null);
        }}
      >
        <FileText className="h-4 w-4" strokeWidth={2} />
      </button>
      {tipPos &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[9999] max-w-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-lg whitespace-pre-wrap break-words"
            style={{ left: tipPos.x + 12, top: tipPos.y + 12 }}
          >
            {tooltipText}
          </div>,
          document.body,
        )}
    </>
  );
}

interface OutcomeTableProps {
  items: OutcomeItem[];
}

export function OutcomeTable({ items }: OutcomeTableProps) {
  const [page, setPage] = useState(0);
  const [notesMap, setNotesMap] = useState<Map<string, string>>(() => new Map());
  const [notesLoading, setNotesLoading] = useState(false);

  const bulkNotesByBillId = useMemo(() => seedNotesMapFromItems(items), [items]);

  const billIdsFetchKey = useMemo(
    () =>
      [...new Set(items.map((i) => String(i.billId || "").trim()).filter(Boolean))].sort().join("|"),
    [items],
  );

  useLayoutEffect(() => {
    if (!billIdsFetchKey) {
      setNotesMap(new Map());
      return;
    }
    setNotesMap(seedNotesMapFromItems(items));
  }, [billIdsFetchKey, items]);

  useEffect(() => {
    if (!billIdsFetchKey) {
      setNotesLoading(false);
      return;
    }
    const allIds = billIdsFetchKey.split("|");
    let cancelled = false;
    setNotesLoading(true);
    fetchBillsNotes(allIds)
      .then((m) => {
        if (cancelled) return;
        setNotesMap((prev) => {
          const next = new Map(prev);
          for (const [k, v] of m) {
            const key = String(k ?? "").trim();
            if (!key) continue;
            const t = String(v ?? "").trim();
            if (!t) continue;
            const prevVal = (next.get(key) || "").trim();
            if (t.length > prevVal.length) next.set(key, t);
          }
          return next;
        });
      })
      .catch(() => {
        /* mantém texto do bulk em prev */
      })
      .finally(() => {
        if (!cancelled) setNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [billIdsFetchKey]);

  const total   = items.length;
  const pages   = Math.max(1, Math.ceil(total / OUTCOME_PER_PAGE));
  const visible = items.slice(page * OUTCOME_PER_PAGE, (page + 1) * OUTCOME_PER_PAGE);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">{total} registro{total !== 1 ? "s" : ""}</p>
      <div className="min-w-0">
        <table className="w-full table-fixed text-sm border-collapse">
          <colgroup>
            <col className="w-[4.75rem]" />
            <col className="w-[5.25rem]" />
            <col />
            <col className="w-[26%]" />
            <col className="w-[6.25rem]" />
            <col className="w-[5.75rem]" />
            <col className="w-10" />
          </colgroup>
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-2 whitespace-nowrap">
                Nº Título
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-2 whitespace-nowrap">
                Vencimento
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-2">
                Credor
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-2">
                Tipo Doc.
              </th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-2 whitespace-nowrap">
                Valor
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-1 whitespace-nowrap">
                Status
              </th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 w-10">
                Notas
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item, i) => {
              const resolvedNotes = noteTextForItem(item, notesMap, bulkNotesByBillId);
              const notesTooltip =
                notesLoading && !resolvedNotes.trim()
                  ? "Carregando observações…"
                  : resolvedNotes.trim() || "Sem observações cadastradas.";
              const docType = item.documentType || "—";
              const creditor = item.creditorName || "—";
              return (
                <tr key={`${item.billId}-${i}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 pr-2 text-slate-500 text-xs whitespace-nowrap font-mono tabular-nums">
                    {item.billId || "—"}
                  </td>
                  <td className="py-2 pr-2 text-slate-700 whitespace-nowrap text-xs">
                    {item.dueDate ? formatDate(item.dueDate) : "—"}
                  </td>
                  <td className="py-2 pr-2 text-slate-800 font-medium truncate text-xs" title={creditor}>
                    {creditor}
                  </td>
                  <td className="py-2 pr-2 text-slate-500 text-xs truncate" title={docType}>
                    {docType}
                  </td>
                  <td className="py-2 pr-2 text-right font-semibold text-slate-800 whitespace-nowrap text-xs tabular-nums">
                    {formatCurrency(outcomeParcelValorExibido(item))}
                  </td>
                  <td className="py-2 pr-1 whitespace-nowrap">
                    <span className={cn(
                      "inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-tight",
                      item.isPaid
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    )}>
                      {item.isPaid ? "Pago" : "Em Aberto"}
                    </span>
                  </td>
                  <td className="py-2 text-center align-middle">
                    <OutcomeNotesIcon tooltipText={notesTooltip} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-slate-400">
            Página {page + 1} de {pages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page >= pages - 1}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Tabela de income (recebimentos) ────────────────────────────────────── */

interface IncomeTableProps {
  items: IncomeItem[];
}

export function IncomeTable({ items }: IncomeTableProps) {
  const [page, setPage] = useState(0);
  const total   = items.length;
  const pages   = Math.max(1, Math.ceil(total / OUTCOME_PER_PAGE));
  const visible = items.slice(page * OUTCOME_PER_PAGE, (page + 1) * OUTCOME_PER_PAGE);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">{total} registro{total !== 1 ? "s" : ""}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Vencimento</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Cliente</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Tipo Doc.</th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Valor</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item, i) => (
                <tr key={`${item.billId}-${i}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 pr-4 text-slate-500 text-xs whitespace-nowrap font-mono">
                    {item.billId || "—"}
                  </td>
                  <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">
                  {item.dueDate ? formatDate(item.dueDate) : "—"}
                </td>
                <td className="py-2 pr-4 text-slate-800 font-medium max-w-[180px] truncate">
                  {item.clientName || "—"}
                </td>
                <td className="py-2 pr-4 text-slate-500 text-xs whitespace-nowrap">
                  {item.documentType || "—"}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-slate-800 whitespace-nowrap">
                  {formatCurrency(item.originalAmount)}
                </td>
                <td className="py-2">
                  <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold",
                    item.isPaid
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-blue-50 text-blue-700"
                  )}>
                    {item.isPaid ? "Recebido" : "A Receber"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-slate-400">
            Página {page + 1} de {pages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page >= pages - 1}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Bulk bank-movement (mesma base do KPI Pagamentos / Recebimentos) ───── */

const BANK_MOV_FIN_PER_PAGE = 25;

interface BankMovementFinanceiroBulkTableProps {
  items: BankMovementFinanceiroLine[];
  /** Débitos (Payments) usa tom vermelho; créditos (Receipts) verde */
  variant: "pagamento" | "recebimento";
}

export function BankMovementFinanceiroBulkTable({
  items,
  variant,
}: BankMovementFinanceiroBulkTableProps) {
  const [page, setPage] = useState(0);
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / BANK_MOV_FIN_PER_PAGE));
  const visible = items.slice(page * BANK_MOV_FIN_PER_PAGE, (page + 1) * BANK_MOV_FIN_PER_PAGE);

  if (total === 0) {
    return (
      <p className="text-xs text-slate-400 py-2">
        Nenhum movimento encontrado nesta base para o período.
      </p>
    );
  }

  const valClass =
    variant === "pagamento" ? "text-rose-700" : "text-emerald-700";

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">
        {total} linha{total !== 1 ? "s" : ""} incluída{total !== 1 ? "s" : ""} no total do card · origem BC (bulk
        bank-movement)
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">
                Data
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">
                Operação
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">
                Histórico
              </th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2">
                Valor
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item) => (
              <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="py-2 pr-4 text-slate-700 whitespace-nowrap text-xs">
                  {item.movementDate ? formatDate(item.movementDate) : "—"}
                </td>
                <td className="py-2 pr-4 text-slate-800 font-medium max-w-[120px] truncate text-xs">
                  {item.operationName || "—"}
                </td>
                <td
                  className="py-2 pr-4 text-slate-600 max-w-[320px] text-xs whitespace-normal break-words leading-snug"
                  title={item.historico}
                >
                  {item.historico || "—"}
                </td>
                <td className={cn("py-2 text-right font-semibold whitespace-nowrap tabular-nums text-xs", valClass)}>
                  {formatCurrency(item.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-slate-400">
            Página {page + 1} de {pages}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page >= pages - 1}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Extrato bancário (SEMOG) ───────────────────────────────────────────── */

interface StatementTableProps {
  items: BankStatementMovement[];
}

export function StatementTable({ items }: StatementTableProps) {
  const [page, setPage] = useState(0);
  const total   = items.length;
  const pages   = Math.max(1, Math.ceil(total / OUTCOME_PER_PAGE));
  const visible = items.slice(page * OUTCOME_PER_PAGE, (page + 1) * OUTCOME_PER_PAGE);

  if (total === 0) {
    return <p className="text-xs text-slate-400 py-2">Nenhum movimento de extrato neste período.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">{total} movimento{total !== 1 ? "s" : ""} (contas SEMOG)</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Data</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Conta</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Histórico</th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4">Crédito</th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2">Débito</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item) => (
              <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">
                  {item.movementDate ? formatDate(item.movementDate) : "—"}
                </td>
                <td className="py-2 pr-4 text-slate-800 font-medium max-w-[140px] truncate text-xs">
                  {item.accountLabel}
                </td>
                <td className="py-2 pr-4 text-slate-600 max-w-[220px] truncate text-xs" title={item.description}>
                  {item.description || "—"}
                </td>
                <td className="py-2 pr-4 text-right text-emerald-700 font-semibold whitespace-nowrap tabular-nums">
                  {item.credit > 0 ? formatCurrency(item.credit) : "—"}
                </td>
                <td className="py-2 text-right text-rose-700 font-semibold whitespace-nowrap tabular-nums">
                  {item.debit > 0 ? formatCurrency(item.debit) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-slate-400">
            Página {page + 1} de {pages}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page >= pages - 1}
              className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Banner de resumo ────────────────────────────────────────────────────── */
interface SummaryBannerProps {
  label:    string;
  value:    string;
  color:    string;
  subtitle?: string;
}

export function SummaryBanner({ label, value, color, subtitle }: SummaryBannerProps) {
  return (
    <div
      className="rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-4"
      style={{ background: `${color}15`, borderLeft: `4px solid ${color}` }}
    >
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-slate-800 mt-0.5">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}
